import { useAuthContext } from '@core/shared';
import { OptionsApi } from '@core/swagger-client';
import { ArrowRight, ErrorMessage, Heading, LoaderPage, ProfileAbbreviation } from '@core/ui';
import { useLocation, useNavigate } from '@online-webinar/router';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isValidPreviousPath } from '../../../hooks/isValidPreviousPath';

export const SwitchAccountPage = () => {
    const { t } = useTranslation();
    const { managedAccounts, mainAccount, updateAuthState } = useAuthContext();
    const navigate = useNavigate();

    const location = useLocation();
    const previousPath = location.state?.from;

    const switchAccountMutation = useMutation(OptionsApi.accountManagement.switchAccount());
    const switchAccount = (accountId: string) => {
        switchAccountMutation.mutate(accountId, {
            onSuccess: (response) => {
                updateAuthState(response);
                navigate({ to: isValidPreviousPath(previousPath) ? previousPath : '/' });
            }
        });
    };

    if (!managedAccounts) {
        return <LoaderPage />;
    }

    return (
        <div className="flex-1 flex flex-col px-4 py-8 md:px-8 lg:px-12">
            <Heading variant="h1" classNames="mb-4">
                {t('AccountSwitch.PageHeading')}
            </Heading>
            {switchAccountMutation.error && <ErrorMessage apiError={switchAccountMutation.error} />}
            <div className="flex-1 max-w-7xl grid content-center sm:justify-center">
                <div className="grid lg:grid-cols-2">
                    <div className="sm:min-w-96 border-b lg:border-b-0 lg:border-e border-gray-500 flex flex-col lg:pe-6 pt-1 pb-8 lg:pb-4">
                        <Heading variant="h2" classNames="mb-4">
                            {t('AccountSwitch.PersonalAccount.Heading')}
                        </Heading>
                        <button
                            onClick={() => switchAccount(mainAccount!.accountId)}
                            disabled={switchAccountMutation.isPending}
                            className="bg-white rounded-button flex items-center outline outline-2 outline-offset-2 outline-transparent hover:outline-primary focus-visible:outline-primary overflow-hidden text-start transition-[outline] ease-in-out duration-200"
                            aria-label={t('AccountSwitch.Button.SwitchAccount', {
                                account: mainAccount!.profile.firstName + ' ' + mainAccount!.profile.lastName
                            })}
                        >
                            <div
                                className="flex-none size-16 bg-account rounded-r-button grid place-items-center text-xl text-white uppercase overflow-hidden"
                                aria-hidden="true"
                            >
                                {!!mainAccount!.assets?.profileImage?.imageUrl && (
                                    <img src={mainAccount!.assets.profileImage.imageUrl} className="w-full h-full object-cover" alt="" />
                                )}
                                {!mainAccount!.assets?.profileImage?.imageUrl && <ProfileAbbreviation profile={mainAccount!.profile} />}
                            </div>
                            <span className="flex-1 ms-4 me-3 truncate">
                                {mainAccount!.profile.firstName} {mainAccount!.profile.lastName}
                            </span>
                            <ArrowRight classNames="size-6 fill-primary me-2" />
                        </button>
                    </div>
                    <div className="sm:min-w-96 flex flex-col lg:ps-6 pt-6 lg:pt-1 pb-4">
                        <Heading variant="h2" classNames="mb-4">
                            {t('AccountSwitch.ManagedAccounts.Heading')}
                        </Heading>
                        {managedAccounts.map((account) => (
                            <button
                                key={account.accountId}
                                onClick={() => switchAccount(account.accountId)}
                                className="bg-white rounded-button flex items-center outline outline-2 outline-offset-2 outline-transparent hover:outline-primary focus-visible:outline-primary overflow-hidden text-start transition-[outline] ease-in-out duration-200 not-last:mb-4"
                                aria-label={t('AccountSwitch.Button.SwitchAccount', { account: account.profile.firstName + ' ' + account.profile.lastName })}
                                disabled={switchAccountMutation.isPending}
                            >
                                <div
                                    className="flex-none size-16 bg-account rounded-r-button grid place-items-center text-xl text-white uppercase overflow-hidden"
                                    aria-hidden="true"
                                >
                                    {!!account.assets?.profileImage?.imageUrl && (
                                        <img src={account.assets.profileImage.imageUrl} className="w-full h-full object-cover" alt="" />
                                    )}
                                    {!account.assets?.profileImage?.imageUrl && <ProfileAbbreviation profile={account.profile} />}
                                </div>
                                <span className="flex-1 ms-4 me-3 truncate">
                                    {account.profile.firstName} {account.profile.lastName}
                                </span>
                                <ArrowRight classNames="size-6 fill-primary me-2" />
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
